// Class colors for a stack legend. The palette is the one the covariate build
// uses, so a color_index paints the same hex on the map and in the layer listing.
// A legend is that build when categories_version is 1, or when the catalog row
// has no version and every class carries code and a color (the catalog relays
// the categories array, not the sibling version). Any other version keeps the
// screen's own legend, so an old stack paints as it does today.
import palette from "../../../shared/class_palette.json";
import type { CategoryEntry, FieldCovariate } from "../types/contract";

export const CLASS_PALETTE: readonly string[] = palette;

export const CATEGORIES_VERSION = 1;
export const MAX_LEGEND_CLASSES = 40;
export const LEGEND_KEEP = 23;
// Neutral for the classes the legend groups. Same hex as the layer listing.
export const OTHER_COLOR = "#9e9e9e";

export type BackendCategory = {
  value: string;
  label: string;
  code: number;
  color?: string;
  color_index?: number;
  cells?: number;
};

type LegendSource = Pick<FieldCovariate, "categories" | "categories_version">;

export function classColor(
  row: { color?: string; color_index?: number },
  fallbackIndex: number,
): string {
  if (row.color) return row.color;
  const index = row.color_index ?? fallbackIndex;
  const n = CLASS_PALETTE.length;
  return CLASS_PALETTE[((index % n) + n) % n];
}

function rowReady(
  row: NonNullable<FieldCovariate["categories"]>[number],
): row is BackendCategory {
  if (row.value == null || row.label == null) return false;
  if (row.code == null || !Number.isFinite(row.code)) return false;
  if (row.color_index != null && !Number.isFinite(row.color_index))
    return false;
  return row.color != null || row.color_index != null;
}

export function backendCategories(cov: LegendSource): BackendCategory[] | null {
  if (
    cov.categories_version != null &&
    cov.categories_version !== CATEGORIES_VERSION
  )
    return null;
  const rows = cov.categories;
  if (!rows?.length) return null;
  const parsed: BackendCategory[] = [];
  for (const row of rows) {
    if (!rowReady(row)) return null;
    const cells =
      row.cells != null && Number.isFinite(row.cells) ? row.cells : undefined;
    parsed.push({
      value: String(row.value),
      label: String(row.label),
      code: row.code,
      color: row.color || undefined,
      color_index: row.color_index,
      cells,
    });
  }
  return parsed;
}

function entryFrom(row: BackendCategory, classValue: number): CategoryEntry {
  return {
    class_value: classValue,
    color: classColor(row, row.code),
    label: row.label,
    code: row.code,
    ...(row.cells != null ? { cells: row.cells } : {}),
  };
}

// Numeric codes match Number(value), so "10" and "10.0" are the same class.
export function numericLegendEntries(
  rows: BackendCategory[],
  values: number[],
): CategoryEntry[] | null {
  const byNumber = new Map<number, BackendCategory>();
  for (const row of rows) {
    const n = Number(row.value);
    if (Number.isFinite(n)) byNumber.set(n, row);
  }
  const entries: CategoryEntry[] = [];
  for (const value of [...new Set(values)].sort((a, b) => a - b)) {
    const row = byNumber.get(value);
    if (!row) return null;
    entries.push(entryFrom(row, value));
  }
  return entries.length ? entries : null;
}

export interface TextLegend {
  entries: CategoryEntry[];
  codeOf: Map<string, number>;
}

// Text matches value as a string, so "10" and "010" stay different classes.
export function textLegendEntries(
  rows: BackendCategory[],
  raw: (string | number | null)[],
): TextLegend | null {
  const byValue = new Map<string, BackendCategory>();
  for (const row of rows) byValue.set(row.value, row);
  const seen = new Map<string, BackendCategory>();
  for (const value of raw) {
    if (value == null) continue;
    const key = String(value);
    const row = byValue.get(key);
    if (!row) return null;
    if (seen.has(key)) continue;
    seen.set(key, row);
  }
  if (!seen.size) return null;
  const ordered = [...seen.values()].sort((a, b) => a.code - b.code);
  const codeOf = new Map<string, number>();
  for (const [key, row] of seen) codeOf.set(key, row.code);
  return {
    entries: paintCategories(ordered.map((row) => entryFrom(row, row.code))),
    codeOf,
  };
}

function bucketsRemainder(entries: CategoryEntry[]): boolean {
  return (
    entries.length > MAX_LEGEND_CLASSES &&
    entries.every((entry) => typeof entry.cells === "number")
  );
}

function byCells(a: CategoryEntry, b: CategoryEntry): number {
  const cells = (b.cells ?? 0) - (a.cells ?? 0);
  if (cells !== 0) return cells;
  return (a.code ?? a.class_value) - (b.code ?? b.class_value);
}

function topByCells(entries: CategoryEntry[]): CategoryEntry[] {
  return [...entries].sort(byCells).slice(0, LEGEND_KEEP);
}

function unusedClassValue(entries: CategoryEntry[]): number {
  const used = new Set(entries.map((entry) => entry.class_value));
  let n = -1;
  while (used.has(n)) n -= 1;
  return n;
}

// Tail classes stay drawable, in one neutral color. Their labels stay, so a
// hover on that cell still names the class.
export function paintCategories(entries: CategoryEntry[]): CategoryEntry[] {
  if (!bucketsRemainder(entries)) return entries;
  const keep = new Set(topByCells(entries).map((entry) => entry.class_value));
  return entries.map((entry) =>
    keep.has(entry.class_value) ? entry : { ...entry, color: OTHER_COLOR },
  );
}

export function legendCategories(entries: CategoryEntry[]): CategoryEntry[] {
  if (!bucketsRemainder(entries)) return entries;
  const top = topByCells(entries);
  const rest = entries.length - top.length;
  return [
    ...top,
    {
      class_value: unusedClassValue(entries),
      color: OTHER_COLOR,
      label: `Other (${rest} classes)`,
    },
  ];
}
